import { type ReactNode } from "react"
import { Linking, TouchableOpacity, View } from "react-native"
import { router } from "expo-router"
import GoldTrophy from "@/assets/clipart/gold-standard-trophy.svg"
import DoubleDouble from "@/assets/clipart/double-double-bills.svg"
import Rocket from "@/assets/clipart/rocket-product.svg"
import Keys from "@/assets/clipart/refi-keys.svg"
import { PrimaryButton } from "../../../components/buttons/CommonButtons"
import { H4, H6, Small, Tiny } from "@/components/standard/BFText"
import { Divider } from "@/screens/loans/components/Divider"
import { loanProductsQuestionClicked } from "@/analytics/events/loans"
import { useScreenName } from "@/hooks/useScreenName"
import { useUserQualifiedForContactLink } from "@/hooks/useUserQualifiedForContactLink"
import { loanSearchPath } from "@/screens/loans/utils/LoanUtils"

interface LoanProductProps {
  title: string
  subtitle: string
  description: string
  image: ReactNode
}

const IMAGE_SIZE = 85

const LoanProduct: React.FC<LoanProductProps> = ({
  title,
  subtitle,
  description,
  image,
}: LoanProductProps) => {
  return (
    <View className="flex flex-row p-3 px-6 justify-between">
      <View className="flex flex-col gap-0.5 flex-1 justify-center">
        <H4 className="text-midnight-blue">{title}</H4>
        <H6 className="text-calm-blue">{subtitle}</H6>
        <Small className="text-midnight-blue">{description}</Small>
      </View>
      <View className="items-center justify-center w-[100px]">{image}</View>
    </View>
  )
}

interface LoanProductsProps {
  onDismiss?: () => void
}

export default function LoanProducts({ onDismiss }: LoanProductsProps) {
  const screenName = useScreenName()
  const qualifiedForContactLink = useUserQualifiedForContactLink()

  return (
    <View className="flex flex-1">
      <TouchableOpacity
        testID={"loan-product-area-action"}
        onPress={() => {
          if (onDismiss) {
            onDismiss()
          }
          router.push(loanSearchPath())
        }}
        hitSlop={12}
      >
        <LoanProduct
          title="The Standard"
          subtitle="Interest Rates from 10%"
          description="Optimize for low fees and interest"
          image={<GoldTrophy height={IMAGE_SIZE} />}
        />
        <Divider />
        <LoanProduct
          title="Double Double"
          subtitle="Up to 93% LTC*"
          description="Minimize your cash to close and maximize leverage"
          image={<DoubleDouble width={IMAGE_SIZE} height={IMAGE_SIZE} />}
        />
        <Divider />
        <LoanProduct
          title="Zero Gravity"
          subtitle="Up to 96% LTC"
          description="No upfront fees, no payment for 6 months, highest leverage"
          image={<Rocket width={IMAGE_SIZE} height={IMAGE_SIZE} />}
        />
        <Divider />
        <LoanProduct
          title="Refi + Rehab"
          subtitle="Up to 85% LTC"
          description="Refinance for rehab projects to maximize ROI"
          image={<Keys height={IMAGE_SIZE} />}
        />
        <View className={"w-full justify-center items-center"}>
          <Tiny className={"text-midnight-blue pt-5 pb-2"}>*at completion</Tiny>
        </View>
      </TouchableOpacity>

      <View className="pb-3 pt-0 flex flex-col justify-center items-center">
        <PrimaryButton
          testID={"apply-button"}
          title="Get A Quote"
          style={{ paddingHorizontal: 64 }}
          onPress={() => {
            if (onDismiss) {
              onDismiss()
            }
            router.push(loanSearchPath())
          }}
        />
        <View className="flex flex-row items-center justify-center">
          {qualifiedForContactLink && (
            <TouchableOpacity
              testID={"questions-lending-expert-button"}
              onPress={() => {
                loanProductsQuestionClicked(screenName)
                void Linking.openURL("https://backflip.app.link/e/WBWTyZcnSLb")
              }}
              hitSlop={4}
            >
              <Tiny className="text-calm-blue text-center underline pt-2 pb-4">
                Questions? Contact a lending expert
              </Tiny>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  )
}
